/**
 * PortalBrandingPreview (Story 14.5 — AC #12)
 *
 * Read-only miniature of the client portal header + footer, re-rendering as
 * the user types in the Branding settings form.
 *
 * NOT a portal-view round-trip — purely a pure component reading the in-progress
 * form state (see D43). Uses the SHARED `computePoweredByProvenLeap` helper so
 * the preview's footer matches exactly what the portal will show (D36).
 */

import {
  computePoweredByProvenLeap,
  getContrastingText,
  type Plan,
} from "@provenleap/types";
import { ArrowClockwise } from "@phosphor-icons/react";
import { ContrastBadge } from "./ContrastBadge";

export interface PortalBrandingPreviewProps {
  branding: {
    logoUrl: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    companyName: string | null;
    helpUrl: string | null;
  };
  plan: Plan;
}

export function PortalBrandingPreview({
  branding,
  plan,
}: PortalBrandingPreviewProps) {
  const { logoUrl, primaryColor, secondaryColor, companyName, helpUrl } =
    branding;
  const displayCompany = companyName ?? "ProvenLeap";
  const powered = computePoweredByProvenLeap(plan);
  const contrastingText = getContrastingText(primaryColor);
  // Story 15.3: mirror PortalShell exactly — the border is driven by
  // secondaryColor (fallback secondary → primary → default), primaryColor stays
  // the text/link accent. Branded when EITHER is set.
  const branded = Boolean(primaryColor || secondaryColor);
  const brandStyle: React.CSSProperties | undefined = branded
    ? ((() => {
        const s: Record<string, string> = {};
        if (primaryColor) s["--portal-accent"] = primaryColor;
        if (secondaryColor) s["--portal-accent-secondary"] = secondaryColor;
        return s as React.CSSProperties;
      })())
    : undefined;

  return (
    <div
      role="img"
      aria-label="Portal branding preview"
      className="rounded-[22px] border-[0.5px] border-[var(--hairline)] bg-card shadow-xs overflow-hidden"
    >
      <div className="px-4 py-2 text-[11px] text-muted-foreground border-b-[0.5px] border-[hsl(214_25%_90%)] bg-[hsl(214_25%_98%)]">
        Preview — this is what your clients will see
      </div>

      {/* Mini header — matches PortalShell's branded pattern exactly so the
          preview reflects what clients actually see (CR3 R3-F1). */}
      <div
        data-portal-branded={branded ? "" : undefined}
        className={
          branded
            ? "relative border-b-[0.5px] portal-accent-secondary-border bg-background"
            : "relative border-b-[0.5px] border-[var(--hairline)] bg-background"
        }
        style={brandStyle}
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {logoUrl ? (
              // Security: SVG branding logos MUST render via <img src>, never
              // <object>/<iframe>/<embed>/inlined. See Story 14.5 AC #4.
              <img
                src={logoUrl}
                alt=""
                className="h-7 w-auto shrink-0"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="text-[13px] font-semibold tracking-tight text-muted-foreground">
                {displayCompany}
              </span>
            )}
            <div className="min-w-0">
              <div className="text-[13px] font-semibold tracking-tight truncate">
                Acme Corp
              </div>
              <div className="text-[11px] text-muted-foreground">
                Q1 2026 Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              role="presentation"
              className="h-7 px-2 inline-flex items-center rounded-md text-[11px] border-[0.5px] border-[var(--hairline)] text-muted-foreground"
            >
              Last 28 days
            </div>
            <div
              role="presentation"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md border-[0.5px] border-[var(--hairline)] text-muted-foreground"
            >
              <ArrowClockwise size={13} />
            </div>
          </div>
        </div>

        {primaryColor ? (
          <div className="absolute right-3 top-3">
            <ContrastBadge colorHex={primaryColor} background="white" />
          </div>
        ) : null}
      </div>

      {/* Sample card with primary color accent */}
      <div className="p-4 space-y-3">
        <div className="rounded-2xl border-[0.5px] border-[hsl(214_25%_90%)] bg-background p-3">
          <div className="text-[11px] text-muted-foreground">Sessions</div>
          <div
            className="text-[22px] font-semibold tracking-tight"
            style={
              primaryColor
                ? ({ color: primaryColor } as React.CSSProperties)
                : undefined
            }
          >
            12,345
          </div>
          <div className="text-[12px] text-muted-foreground mt-1">
            Dashboard for Acme Corp
          </div>
        </div>
      </div>

      {/* Footer — matches PortalShell's branded pattern (CR3 R3-F1). */}
      <div
        data-portal-branded={branded ? "" : undefined}
        className={
          branded
            ? "px-4 py-3 flex items-center justify-between text-[11px] text-muted-foreground border-t-[0.5px] portal-accent-secondary-border"
            : "px-4 py-3 flex items-center justify-between text-[11px] text-muted-foreground border-t-[0.5px] border-[hsl(214_25%_90%)]"
        }
        style={brandStyle}
      >
        {/* LEFT group mirrors the live portal footer exactly (CR3 R3-F1): the
            agency "Help" link (when helpUrl set) + the gated powered-by/company,
            with the expiry on the RIGHT. Rendered as spans — the preview is a
            read-only role=img with no real links. */}
        <span className="flex items-center gap-3">
          {helpUrl?.startsWith("https://") ? (
            <span className="underline underline-offset-2">Help</span>
          ) : null}
          {powered ? (
            <span
              className={primaryColor ? "portal-accent-text" : undefined}
              style={
                primaryColor
                  ? ({ color: contrastingText } as React.CSSProperties)
                  : undefined
              }
            >
              Powered by ProvenLeap
            </span>
          ) : (
            <span>{displayCompany}</span>
          )}
        </span>
        <span>Your agency link expires in 14 days.</span>
      </div>
    </div>
  );
}
